import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import {Modal, Button, Table} from "react-bootstrap";
import { useReactToPrint } from 'react-to-print';
import {RREmployeeType} from "../types/RREmployeeType";
import {dateToString, timeToString} from "../tools";
import {PrintIconButton} from "./IconButtons";
import useRoadBookCrud from "../hooks/useRoadBookCrud";
import { showMsgInToast, showErrorsListInToast } from '../tools';
import {RRLogisticType} from "../views/logistics/RRLogisticType";

interface RoadBookPreviewProps {
    show: boolean;
    employee?: RREmployeeType;
    date?: Date;
    logistics?: RRLogisticType[];
    onClose: () => void;
    onPrint?: () => void;
}

export default function RoadBookPreview({show, employee, date, logistics, onClose, onPrint}: RoadBookPreviewProps): JSX.Element {
    const formattedDate = date ? dateToString(date) : '';

    // Extract unique vehicles (truck objects) from logistics, deduped by plate
    const uniqueVehicles = (() => {
        const map = new Map<string, any>();
        if (!logistics) return [] as any[];
        logistics.forEach(lg => {
            const t = lg.truck;
            if (t && t.plate && t.plate.toString().trim() !== '') {
                if (!map.has(t.plate)) map.set(t.plate, t);
            }
        });
        return Array.from(map.values());
    })();

    // selectedPlate stores the chosen vehicle's plate; dropdown shows vehicle.name
    // If there's more than one vehicle, don't auto-select — require user to pick one.
    const [selectedPlate, setSelectedPlate] = useState<string>(uniqueVehicles.length === 1 ? (uniqueVehicles[0]?.plate || '') : '');

    // derive a primitive key for uniqueVehicles to use in effect deps (avoids complex expressions)
    const vehiclePlates = uniqueVehicles.map((v: any) => v.plate).join(',');

    // Keep selectedPlate in sync when the available vehicles list changes.
    // Use functional updater so we don't include `selectedPlate` in deps (avoids update loop).
    useEffect(() => {
        setSelectedPlate(prev => {
            if (!uniqueVehicles || uniqueVehicles.length === 0) return '';
            if (uniqueVehicles.length === 1) return uniqueVehicles[0].plate || '';
            // keep previous selection if it's still present; otherwise reset
            const exists = uniqueVehicles.some((v: any) => v.plate === prev);
            return exists ? prev : '';
        });
    }, [vehiclePlates, uniqueVehicles]);

    // Prepare filtered logistics sets to be used both for header (km summary) and rows
    const allFilteredLogistics = useMemo(() => {
        return logistics ? logistics.filter(lg => lg.truck && lg.truck.plate && lg.truck.plate.toString().trim() !== '') : [];
    }, [logistics]);

    const visibleLogisticsForHeader = useMemo(() => {
        return selectedPlate ? allFilteredLogistics.filter(lg => lg.truck.plate === selectedPlate) : allFilteredLogistics;
    }, [allFilteredLogistics, selectedPlate]);

    // compute min startKm and max endKm from all filtered logistics (all records with truck plate)
    const startKmValues = allFilteredLogistics.map(lg => lg.startKm).filter((k): k is number => k !== null && k !== undefined);
    const endKmValues = allFilteredLogistics.map(lg => lg.endKm).filter((k): k is number => k !== null && k !== undefined);
    const minStartKm = startKmValues.length ? Math.min(...startKmValues) : null;
    const maxEndKm = endKmValues.length ? Math.max(...endKmValues) : null;

    // Editable form state (UI stores formNumber as string for easy editing, but backend expects number)
    const [formNumber, setFormNumber] = useState<string>('');
    const [driverName, setDriverName] = useState<string>(employee?.name ?? '');

    // KM inputs (use computed values as initial)
    const [startKmInput, setStartKmInput] = useState<string>(minStartKm !== null ? String(minStartKm) : '');
    const [endKmInput, setEndKmInput] = useState<string>(maxEndKm !== null && maxEndKm > 0 ? String(maxEndKm) : '');

    // Table rows editable state
    const [rowsState, setRowsState] = useState<Array<{id?: string, route: string, start: string, end: string, duration: string, verified: boolean}>>([]);

    // Track saved document id and a snapshot string to detect unsaved changes
    const [savedId, setSavedId] = useState<string | null>(null);
    const [savedSnapshot, setSavedSnapshot] = useState<string | null>(null);

    // sync driver when prop changes
    useEffect(() => setDriverName(employee?.name ?? ''), [employee]);

    // sync km inputs when computed mins/max change
    useEffect(() => {
        setStartKmInput(minStartKm !== null ? String(minStartKm) : '');
        setEndKmInput(maxEndKm !== null && maxEndKm > 0 ? String(maxEndKm) : '');
    }, [minStartKm, maxEndKm]);

    // initialize rowsState from visibleLogisticsForHeader whenever vehicle list or selection changes
    useEffect(() => {
        const initial = (visibleLogisticsForHeader || []).map(lg => ({
            id: lg.id,
            route: (lg.startPoint && lg.endPoint) ? `${lg.startPoint} - ${lg.endPoint}` : (lg.eventNumber || ''),
            start: timeToString(lg.reportStartDate ?? lg.startDate),
            end: timeToString(lg.reportEndDate ?? lg.endDate),
            duration: '',
            verified: false
        }));
        setRowsState(initial);
    }, [vehiclePlates, selectedPlate, visibleLogisticsForHeader]);

    // Number placeholder — the form shows a number and the date; number is left blank for manual fill
    const numberPlaceholder = '№ ____';

    // Render a few empty rows for manual entry in the route table
    const emptyRows = Array.from({length: 10});

    // (calendar/events fallback not used in this component)

    // Hook for CRUD
    const { saveRoadBook, findByFormNumber, getNextFormNumber, getNextFormNumberAtomic, reserveRoadBooksAtomically, findByEmployeeDate, loading: saving } = useRoadBookCrud();

    // Refs/state used by effects
    const [savedDocsByPlate, setSavedDocsByPlate] = useState<Record<string, any>>({});
    const lastReserveKeyRef = useRef<string | null>(null);
    const lastLoadKeyRef = useRef<string | null>(null);
    const isReservingRef = useRef(false);
    const isLoadingRef = useRef(false);

    // Debug counters and wrapped DB function to trace repeated calls; memoize with useCallback so identity is stable
    const dbCallCountersRef = useRef({ findByEmployeeDate: 0, reserveRoadBooksAtomically: 0 });
    const lastDbCallRef = useRef<{ fn?: string, time?: string, args?: any }>({});

    const debugFindByEmployeeDate = useCallback(async (employeeId: string, dt: Date, vehiclePlate?: string) => {
        dbCallCountersRef.current.findByEmployeeDate++;
        lastDbCallRef.current = { fn: 'findByEmployeeDate', time: new Date().toISOString(), args: { employeeId, date: dt?.toISOString?.(), vehiclePlate } };
        console.debug('[RoadBookPreview][DB] calling findByEmployeeDate', { count: dbCallCountersRef.current.findByEmployeeDate, lastCall: lastDbCallRef.current });
        try {
            const r = await findByEmployeeDate(employeeId, dt, vehiclePlate);
            console.debug('[RoadBookPreview][DB] findByEmployeeDate result', { exists: !!r, id: r?.id });
            return r;
        } catch (e) {
            console.debug('[RoadBookPreview][DB] findByEmployeeDate error', e);
            throw e;
        }
    }, [findByEmployeeDate]);

    const debugReserveRoadBooksAtomically = useCallback(async (employeeId: string, dt: Date, payloads: any[]) => {
        dbCallCountersRef.current.reserveRoadBooksAtomically++;
        lastDbCallRef.current = { fn: 'reserveRoadBooksAtomically', time: new Date().toISOString(), args: { employeeId, date: dt?.toISOString?.(), payloadsCount: payloads?.length } };
        console.debug('[RoadBookPreview][DB] calling reserveRoadBooksAtomically', { count: dbCallCountersRef.current.reserveRoadBooksAtomically, lastCall: lastDbCallRef.current });
        try {
            const r = await reserveRoadBooksAtomically(employeeId, dt, payloads);
            console.debug('[RoadBookPreview][DB] reserveRoadBooksAtomically result', { created: r?.length });
            return r;
        } catch (e) {
            console.debug('[RoadBookPreview][DB] reserveRoadBooksAtomically error', e);
            throw e;
        }
    }, [reserveRoadBooksAtomically]);

    // When modal opens, try to load existing roadbook for employee+date (legacy or any vehicle)
    useEffect(() => {
        // NOTE: intentionally narrow dependency array to avoid rerunning due to function identity changes
        // eslint-disable-next-line react-hooks/exhaustive-deps
        let cancelled = false;
        async function loadIfExists() {
            const key = `${employee?.id || ''}_${date ? date.toISOString().slice(0,10) : ''}`;
            console.debug('[RoadBookPreview] loadIfExists start', { time: new Date().toISOString(), key, show });
             if (!show) return;
             if (!employee || !employee.id || !date) return;
             try {
                console.debug('[RoadBookPreview] calling findByEmployeeDate (legacy load)');
                const existing = await debugFindByEmployeeDate(employee.id, date);
                if (cancelled) return;
                if (existing) {
                    console.debug('[RoadBookPreview] existing roadbook found (legacy)', { id: existing.id, formNumber: existing.formNumber });
                     setSavedId(existing.id || null);
                     setFormNumber(existing.formNumber != null ? String(existing.formNumber) : '');
                     if (existing.vehiclePlate) setSelectedPlate(existing.vehiclePlate);
                     setDriverName(existing.employeeName || employee.name || '');
                     setStartKmInput(existing.startKm != null ? String(existing.startKm) : '');
                     setEndKmInput(existing.endKm != null ? String(existing.endKm) : '');
                     // map rows
                     const mapped = (existing.rows || []).map((r: any) => ({
                        id: r.id,
                        route: r.route || '',
                        start: r.start || '',
                        end: r.end || '',
                        duration: r.duration || '',
                        verified: !!r.verified
                    }));
                    setRowsState(mapped.length ? mapped : []);
                    setSavedSnapshot(JSON.stringify({ ...existing, id: existing.id }));
                } else {
                    console.debug('[RoadBookPreview] no legacy roadbook found');
                    setSavedId(null);
                    setSavedSnapshot(null);
                }
             } catch (e: any) {
                 showErrorsListInToast('Грешка при зареждане', [String((e as any)?.message ?? e)]);
             }
         }
         loadIfExists();
         return () => { cancelled = true; }
     }, [show, employee?.id, date, debugFindByEmployeeDate, employee]);

    // When modal opens and there are multiple vehicles, ensure each vehicle has a roadbook: load existing or reserve new ones atomically.
    // We intentionally do not include `savedDocsByPlate` in deps to avoid re-running the reservation loop after we set it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
         let cancelled = false;
         async function reserveMissingForAll() {
              // guard: avoid re-running for same employee/date/vehicles
             const key = `${employee?.id || ''}_${date ? date.toISOString().slice(0,10) : ''}_${uniqueVehicles.map((v:any)=>v.plate).join(',')}`;
             console.debug('[RoadBookPreview] reserveMissingForAll start', { time: new Date().toISOString(), key, lastKey: lastReserveKeyRef.current, show, uniqueVehiclesCount: uniqueVehicles?.length });
             if (lastReserveKeyRef.current === key) { console.debug('[RoadBookPreview] reserveMissingForAll skipped: same key'); return; }
             if (isReservingRef.current) { console.debug('[RoadBookPreview] reserveMissingForAll skipped: already running'); return; }
             if (!show) return;
             if (!employee || !employee.id || !date) return;
             if (!uniqueVehicles || uniqueVehicles.length <= 1) return;
            // mark as running (outer guard)
            isReservingRef.current = true;

            const plates = uniqueVehicles.map((v: any) => v.plate);
            const existingMap: Record<string, any> = {};
            const missingPlates: string[] = [];

            // check existing docs for each plate
            for (const plate of plates) {
                try {
                    const docObj = await debugFindByEmployeeDate(employee.id, date, plate);
                    if (docObj) {
                        existingMap[plate] = { ...docObj, id: docObj.id };
                    } else {
                        missingPlates.push(plate);
                    }
                } catch (e) {
                    // treat as missing
                    missingPlates.push(plate);
                }
            }

            if (cancelled) return;

            // If there are missing, prepare payloads and reserve atomically
            if (missingPlates.length > 0) {
                const payloads = missingPlates.map(plate => {
                    // rows for this plate
                    const rows = (allFilteredLogistics.filter(lg => lg.truck.plate === plate) || []).map(lg => ({
                        id: lg.id,
                        route: (lg.startPoint && lg.endPoint) ? `${lg.startPoint} - ${lg.endPoint}` : (lg.eventNumber || ''),
                        start: timeToString(lg.reportStartDate ?? lg.startDate),
                        end: timeToString(lg.reportEndDate ?? lg.endDate),
                    }));

                    // compute start/end km for this plate
                    const startKms = allFilteredLogistics.filter(lg => lg.truck.plate === plate).map(lg => lg.startKm).filter((k): k is number => k != null);
                    const endKms = allFilteredLogistics.filter(lg => lg.truck.plate === plate).map(lg => lg.endKm).filter((k): k is number => k != null);
                    const startKm = startKms.length ? Math.min(...startKms) : null;
                    const endKm = endKms.length ? Math.max(...endKms) : null;

                    return { vehiclePlate: plate, payload: { employeeName: driverName || employee.name, startKm, endKm, rows } };
                });

                try {
                    console.debug('[RoadBookPreview] calling reserveRoadBooksAtomically', { payloadCount: payloads.length });
                    const reserved = await debugReserveRoadBooksAtomically(employee.id, date, payloads);
                    if (cancelled) return;
                    // merge reserved into existingMap
                    reserved.forEach(r => {
                        const found = payloads.find(p => p.vehiclePlate === r.vehiclePlate);
                        const payload = found ? found.payload : { employeeName: '', startKm: null, endKm: null, rows: [] };
                        existingMap[r.vehiclePlate] = { id: r.id, formNumber: r.formNumber, employeeName: payload.employeeName, startKm: payload.startKm, endKm: payload.endKm, rows: payload.rows };
                    });
                } catch (e: any) {
                    showErrorsListInToast('Грешка при масивна резервация', [String((e as any)?.message ?? e)]);
                } finally {
                    // clear running flag for this effect
                    isReservingRef.current = false;
                    console.debug('[RoadBookPreview] reserveMissingForAll finished (finally), clearing isReservingRef');
                }
            }

            if (cancelled) return;

            console.debug('[RoadBookPreview] finished reserveMissingForAll, setting savedDocsByPlate', { plates: Object.keys(existingMap) });
            setSavedDocsByPlate(existingMap);

            // mark reserved so we don't repeat for the same key
            lastReserveKeyRef.current = key;

            // if a vehicle is selected, populate UI from mapping
            if (selectedPlate && existingMap[selectedPlate]) {
                const docForSelected = existingMap[selectedPlate];
                setSavedId(docForSelected.id || null);
                setFormNumber(docForSelected.formNumber != null ? String(docForSelected.formNumber) : '');
                setDriverName(docForSelected.employeeName || employee.name || '');
                setStartKmInput(docForSelected.startKm != null ? String(docForSelected.startKm) : '');
                setEndKmInput(docForSelected.endKm != null ? String(docForSelected.endKm) : '');
                setRowsState((docForSelected.rows || []).map((r: any) => ({ id: r.id, route: r.route || '', start: r.start || '', end: r.end || '', duration: r.duration || '', verified: !!r.verified })));
                setSavedSnapshot(JSON.stringify({ ...docForSelected, id: docForSelected.id }));
            }
        }

        reserveMissingForAll();
        // ensure flag cleared when effect exits
        return () => { cancelled = true; isReservingRef.current = false; }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show, uniqueVehicles.map(v=>v.plate).join(','), employee?.id, date, JSON.stringify(allFilteredLogistics)]);

    // When user selects a vehicle (or vehicle is preselected), load the roadbook for that vehicle specifically
    useEffect(() => {
         let cancelled = false;
         async function loadForVehicle() {
            const key = `${employee?.id || ''}_${date ? date.toISOString().slice(0,10) : ''}_${selectedPlate || ''}`;
            console.debug('[RoadBookPreview] loadForVehicle start', { time: new Date().toISOString(), key, lastKey: lastLoadKeyRef.current, selectedPlate });
            if (lastLoadKeyRef.current === key) { console.debug('[RoadBookPreview] loadForVehicle skipped: same key'); return; }
            if (isLoadingRef.current) { console.debug('[RoadBookPreview] loadForVehicle skipped: already running'); return; }
            if (!show) return;
            if (!employee || !employee.id || !date) return;
            if (!selectedPlate) return;
            // mark as running
            isLoadingRef.current = true;

            try {
                console.debug('[RoadBookPreview] calling findByEmployeeDate for selectedPlate', { selectedPlate });
                const existing = await debugFindByEmployeeDate(employee.id, date, selectedPlate);
                if (cancelled) return;
                if (existing) {
                    console.debug('[RoadBookPreview] existing roadbook found for selectedPlate', { id: existing.id, formNumber: existing.formNumber });
                    setSavedId(existing.id || null);
                    setFormNumber(existing.formNumber != null ? String(existing.formNumber) : '');
                    setDriverName(existing.employeeName || employee.name || '');
                    setStartKmInput(existing.startKm != null ? String(existing.startKm) : '');
                    setEndKmInput(existing.endKm != null ? String(existing.endKm) : '');
                    const mappedRows = (existing.rows || []).map((r: any) => ({
                        id: r.id,
                        route: r.route || '',
                        start: r.start || '',
                        end: r.end || '',
                        duration: r.duration || '',
                        verified: !!r.verified
                    }));
                    setRowsState(mappedRows.length ? mappedRows : []);
                    setSavedSnapshot(JSON.stringify({ ...existing, id: existing.id }));
                    // mark loaded
                    lastLoadKeyRef.current = key;
                    return;
                }

                // No existing doc for this selected vehicle: create & reserve one atomically with rows from logistics
                console.debug('[RoadBookPreview] no roadbook found for selectedPlate, will attempt to reserve');
                const rows = (allFilteredLogistics.filter(lg => lg.truck.plate === selectedPlate) || []).map(lg => ({
                    id: lg.id,
                    route: (lg.startPoint && lg.endPoint) ? `${lg.startPoint} - ${lg.endPoint}` : (lg.eventNumber || ''),
                    start: timeToString(lg.reportStartDate ?? lg.startDate),
                    end: timeToString(lg.reportEndDate ?? lg.endDate)
                }));

                const startKms = allFilteredLogistics.filter(lg => lg.truck.plate === selectedPlate).map(lg => lg.startKm).filter((k): k is number => k != null);
                const endKms = allFilteredLogistics.filter(lg => lg.truck.plate === selectedPlate).map(lg => lg.endKm).filter((k): k is number => k != null);
                const startKm = startKms.length ? Math.min(...startKms) : null;
                const endKm = endKms.length ? Math.max(...endKms) : null;

                try {
                    console.debug('[RoadBookPreview] calling reserveRoadBooksAtomically for selectedPlate', { selectedPlate, rowsCount: rows.length, startKm, endKm });
                    const reserved = await debugReserveRoadBooksAtomically(employee.id, date, [{ vehiclePlate: selectedPlate, payload: { employeeName: driverName || employee.name, startKm, endKm, rows } }]);
                    if (cancelled) return;
                    const r = reserved && reserved.length ? reserved[0] : null;
                    if (r) {
                        setSavedId(r.id);
                        setFormNumber(r.formNumber != null ? String(r.formNumber) : '');
                        setDriverName(driverName || employee.name || '');
                        setStartKmInput(startKm != null ? String(startKm) : '');
                        setEndKmInput(endKm != null ? String(endKm) : '');
                        // initialize rows state from rows array
                        const mappedRows = (rows || []).map((rr: any) => ({ id: rr.id, route: rr.route || '', start: rr.start || '', end: rr.end || '', duration: '', verified: false }));
                        setRowsState(mappedRows.length ? mappedRows : []);
                        setSavedSnapshot(JSON.stringify({ id: r.id, formNumber: r.formNumber, vehiclePlate: selectedPlate, rows }));
                        showMsgInToast('Създаден е нов пътен лист № ' + (r.formNumber != null ? String(r.formNumber) : ''));
                        // mark loaded/created for this selected vehicle so we don't repeat
                        lastLoadKeyRef.current = key;
                    }
                } catch (e: any) {
                    showErrorsListInToast('Грешка при създаване на пътен лист', [String((e as any)?.message ?? e)]);
                }

            } catch (e: any) {
                showErrorsListInToast('Грешка при зареждане', [String((e as any)?.message ?? e)]);
            } finally {
                isLoadingRef.current = false;
                console.debug('[RoadBookPreview] loadForVehicle finished (finally), clearing isLoadingRef');
            }
        }
        loadForVehicle();
        return () => { cancelled = true; isLoadingRef.current = false; }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show, selectedPlate, employee?.id, date, JSON.stringify(allFilteredLogistics), JSON.stringify(Object.keys(savedDocsByPlate))]);

    // reset refs when modal is closed or context changes to allow re-run when appropriate
    useEffect(() => {
         if (!show) {
            lastReserveKeyRef.current = null;
            lastLoadKeyRef.current = null;
            console.debug('[RoadBookPreview] modal closed, reset lastReserveKeyRef and lastLoadKeyRef');
         }
     }, [show]);

    const buildPayload = useCallback(() => {
        return {
            employeeId: employee?.id,
            employeeName: driverName || employee?.name,
            date: date ?? new Date(),
            formNumber: formNumber ? Number(formNumber) : undefined,
            vehiclePlate: selectedPlate || undefined,
            startKm: startKmInput ? Number(startKmInput) : null,
            endKm: endKmInput ? Number(endKmInput) : null,
            rows: rowsState.map(r => ({ id: r.id, route: r.route, start: r.start, end: r.end, duration: r.duration, verified: r.verified }))
        };
    }, [employee, driverName, date, formNumber, selectedPlate, startKmInput, endKmInput, rowsState]);

    const handleSave = useCallback(async () => {
        if (!employee || !employee.id) {
            alert('Няма служител за запазване');
            return;
        }

        // determine numeric form number (fnNumber). UI stores formNumber as string, backend uses numbers.
        let fnNumber: number | undefined = (formNumber && formNumber.trim() !== '') ? Number(formNumber.trim()) : undefined;
        if (!fnNumber) {
            try {
                fnNumber = await getNextFormNumberAtomic(selectedPlate);
            } catch (e) {
                fnNumber = await getNextFormNumber(selectedPlate);
            }
        }

        // ensure uniqueness: if form number exists for this vehicle, increment until unique
        if (fnNumber != null) {
            const empId = employee?.id;
            let existing = await findByFormNumber(fnNumber, empId, date, selectedPlate);
            let candidate = Number(fnNumber);
            while (existing) {
                candidate = (Number.isNaN(candidate) ? 1 : candidate + 1);
                fnNumber = candidate;
                existing = await findByFormNumber(fnNumber, empId, date, selectedPlate);
            }
            // set the formNumber UI to resolved unique value
            setFormNumber(String(fnNumber));
        }

        const payload = buildPayload();

        try {
            const id = await saveRoadBook(payload as any);
            // remember saved id and snapshot for change detection
            setSavedId(id ?? null);
            setSavedSnapshot(JSON.stringify({ ...payload, id }));
            setFormNumber((prev)=>prev);
            showMsgInToast('Пътният лист е записан успешно.');
        } catch (e) {
            showErrorsListInToast('Грешка при запазване', [String((e as any)?.message ?? e)]);
        }
    }, [buildPayload, findByFormNumber, getNextFormNumberAtomic, getNextFormNumber, saveRoadBook, date, employee, formNumber, selectedPlate]);

    // determine whether there are unsaved changes: compare current payload to savedSnapshot
    const currentSnapshot = JSON.stringify({ ...buildPayload(), id: savedId });
    const hasChanges = !savedSnapshot || savedSnapshot !== currentSnapshot;

    // Save changes if there are any unsaved changes and not already saving
    const saveIfNeeded = useCallback(() => {
        if (hasChanges && !saving) {
            handleSave();
        }
    }, [hasChanges, saving, handleSave]);

    // react-to-print integration
    const printRef = useRef<HTMLDivElement | null>(null);
    const handlePrint = useReactToPrint({
        content: () => printRef.current,
        documentTitle: `Пътен лист ${formNumber || ''} ${formattedDate}`,
        onBeforeGetContent: async () => {
            // ensure latest state is saved before printing
            if (hasChanges && !saving) {
                await handleSave();
            }
            return Promise.resolve();
        }
    });

    return (
        <Modal show={show} onHide={onClose} centered size="lg">
            <Modal.Header closeButton>
                <Modal.Title className={"w-100 text-center"}>
                    <div className={"fw-bold fs-3"}>ПЪТЕН ЛИСТ</div>
                    <div className={"text-muted small d-flex justify-content-center align-items-center"}>
                        <input
                            className="form-control form-control-sm me-2"
                            style={{width: '120px'}}
                            value={formNumber}
                            onChange={(e) => setFormNumber(e.target.value)}
                            placeholder={numberPlaceholder}
                        />
                        / {formattedDate}
                    </div>
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className={"container-fluid"}>
                    <div className={"row align-items-center py-3"}>
                        <div className={"col-6 d-flex align-items-center"}>
                            <strong>Автомобил:</strong>
                            {uniqueVehicles.length === 1 ? (
                                <span className="ms-2">{uniqueVehicles[0].name}</span>
                            ) : (
                                <select
                                    className="form-select ms-2"
                                    value={selectedPlate}
                                    onChange={(e) => setSelectedPlate(e.target.value)}
                                >
                                    {uniqueVehicles.map((vehicle: any, idx: number) => (
                                        <option key={idx} value={vehicle.plate}>{vehicle.name}</option>
                                    ))}
                                </select>
                            )}
                        </div>
                        <div className={"col-6"}>
                            <strong>Рег. №:</strong> <span>{selectedPlate}</span>
                        </div>
                    </div>
                    { (uniqueVehicles.length <= 1 || selectedPlate) ? (
                        <>
                            <div className={"row py-3"}>
                                <div className={"col-12 d-flex align-items-center"}>
                                    <strong>Водач:</strong>
                                    <input
                                        className="form-control form-control-sm ms-2"
                                        style={{maxWidth: '320px'}}
                                        value={driverName}
                                        onChange={(e) => setDriverName(e.target.value)}
                                        onBlur={() => saveIfNeeded()}
                                    />
                                </div>
                            </div>
                            <div className={"row py-3"}>
                                <div className={"col-6 d-flex align-items-center"}>
                                    <strong>Километраж начален:</strong>
                                    <input className="form-control form-control-sm ms-2" style={{width: '140px'}} value={startKmInput} onChange={(e)=>setStartKmInput(e.target.value)} onBlur={() => saveIfNeeded()} />
                                </div>
                                <div className={"col-6 d-flex align-items-center"}>
                                     <strong>краен:</strong>
                                    <input className="form-control form-control-sm ms-2" style={{width: '140px'}} value={endKmInput} onChange={(e)=>setEndKmInput(e.target.value)} onBlur={() => saveIfNeeded()} />
                                 </div>
                             </div>

                            <Table bordered size={"sm"} className={"text-center"}>
                                <thead>
                                <tr>
                                    <th>Маршрут (от-до)</th>
                                    <th>Тръгване (ч.:мин.)</th>
                                    <th>Пристигане (ч.:мин.)</th>
                                    <th>Престой (ч.:мин.)</th>
                                    <th>Заверка</th>
                                </tr>
                                </thead>
                                <tbody>
                                {rowsState && rowsState.length > 0 ? (
                                    rowsState.map((r, idx) => (
                                        <tr key={r.id || idx} style={{height: '48px'}} className={"align-middle"}>
                                            <td className={"text-start px-2 py-2"}>
                                                <input className="form-control form-control-sm" value={r.route} onChange={(e)=>{
                                                    const copy = [...rowsState]; copy[idx] = {...copy[idx], route: e.target.value}; setRowsState(copy);
                                                }} onBlur={() => saveIfNeeded()}/>
                                            </td>
                                            <td className={"py-2"}>
                                                <input type="time" className="form-control form-control-sm" value={r.start || ''} onChange={(e)=>{
                                                    const copy = [...rowsState]; copy[idx] = {...copy[idx], start: e.target.value}; setRowsState(copy);
                                                }} onBlur={() => saveIfNeeded()}/>
                                            </td>
                                            <td className={"py-2"}>
                                                <input type="time" className="form-control form-control-sm" value={r.end || ''} onChange={(e)=>{
                                                    const copy = [...rowsState]; copy[idx] = {...copy[idx], end: e.target.value}; setRowsState(copy);
                                                }} onBlur={() => saveIfNeeded()}/>
                                            </td>
                                            <td className={"py-2"}>
                                                <input className="form-control form-control-sm" value={r.duration} onChange={(e)=>{
                                                    const copy = [...rowsState]; copy[idx] = {...copy[idx], duration: e.target.value}; setRowsState(copy);
                                                }} onBlur={() => saveIfNeeded()}/>
                                            </td>
                                            <td className={"py-2 text-center"}>
                                                <input type="checkbox" checked={r.verified} onChange={(e)=>{
                                                    const copy = [...rowsState]; copy[idx] = {...copy[idx], verified: e.target.checked}; setRowsState(copy);
                                                }} onBlur={() => saveIfNeeded()}/>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    emptyRows.map((_, idx) => (
                                        <tr key={idx} style={{height: '48px'}} className={"align-middle"}>
                                            <td className={"text-start px-2 py-2"}>&nbsp;</td>
                                            <td className={"py-2"}>&nbsp;</td>
                                            <td className={"py-2"}>&nbsp;</td>
                                            <td className={"py-2"}>&nbsp;</td>
                                            <td className={"py-2"}>&nbsp;</td>
                                        </tr>
                                    ))
                                )}
                                </tbody>
                            </Table>
                        </>
                    ) : (
                        <div className={"row my-4"}>
                            <div className={"col-12 text-center text-muted"}>Моля, изберете автомобил, за да попълните останалите полета.</div>
                        </div>
                    )}

                    {/* Signature and stamp are intentionally omitted from the on-screen modal
                        and are rendered only inside the printable layout (printRef) so they
                        appear only on printed documents. */}
                 </div>
             </Modal.Body>
            <Modal.Footer>
                {/* Print button (shown as requested). */}
                <span className={"me-2"}>
                    <PrintIconButton small onClick={() => (onPrint ? onPrint() : handlePrint())} />
                </span>
                <Button variant={"outline-secondary"} onClick={onClose}>Затвори</Button>
            </Modal.Footer>
            {/* Hidden print layout placed off-screen so react-to-print can access it */}
            <div style={{ position: 'fixed', left: '-10000px', top: 0, width: '1px', height: '1px', overflow: 'hidden' }}>
                <div ref={printRef} id="roadbook-print-root">
                    <div className={"container-fluid p-4"}>
                        {/* Title for printed document */}
                        <div className={"row m-5"}>
                            <div className={"col-12 text-center"}>
                                <div style={{fontSize: '22px', fontWeight: 700}} className={"mb-3"}>ПЪТЕН ЛИСТ</div>
                                <div style={{fontSize: '12px', color: '#333'}}>{formNumber ? `№ ${formNumber}` : ''} {formNumber ? '/' : ''} {formattedDate}</div>
                            </div>
                        </div>

                        <div className={"row mb-2 align-items-center"}>
                            <div className={"col-6 d-flex align-items-center"}>
                                <strong>Автомобил:</strong>
                                <span className="ms-2">{(uniqueVehicles.length === 1 ? uniqueVehicles[0].name : (uniqueVehicles.find((v:any)=>v.plate===selectedPlate)?.name || '')) || ''}</span>
                            </div>
                            <div className={"col-6"}>
                                <strong>Рег. №:</strong> <span>{selectedPlate || ''}</span>
                            </div>
                        </div>

                        {(uniqueVehicles.length <= 1 || selectedPlate) ? (
                            <>
                                <div className={"row mb-2"}>
                                    <div className={"col-12 d-flex align-items-center"}>
                                        <strong>Водач:</strong>
                                        <span className="ms-2">{driverName}</span>
                                    </div>
                                </div>
                                <div className={"row mb-3"}>
                                    <div className={"col-6 d-flex align-items-center"}>
                                        <strong>Километраж начален:</strong>
                                        <span className="ms-2">{startKmInput}</span>
                                    </div>
                                    <div className={"col-6 d-flex align-items-center"}>
                                        <strong>Километраж краен:</strong>
                                        <span className="ms-2">{endKmInput !== "0" ? endKmInput : ''}</span>
                                    </div>
                                </div>

                                <Table bordered size={"sm"} className={"text-center"}>
                                    <thead>
                                    <tr>
                                        <th>Маршрут (от-до)</th>
                                        <th>Тръгване (ч.:мин.)</th>
                                        <th>Пристигане (ч.:мин.)</th>
                                        <th>Престой (ч.:мин.)</th>
                                        <th>Заверка</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {rowsState && rowsState.length > 0 ? (
                                        rowsState.map((r, idx) => (
                                            <tr key={r.id || idx} style={{height: '48px'}} className={"align-middle"}>
                                                <td className={"text-start px-2 py-2"}>
                                                    {r.route}
                                                </td>
                                                <td className={"py-2"}>
                                                    {r.start || ''}
                                                </td>
                                                <td className={"py-2"}>
                                                    {r.end || ''}
                                                </td>
                                                <td className={"py-2"}>
                                                    {r.duration || ''}
                                                </td>
                                                <td className={"py-2 text-center"}>
                                                    {r.verified ? '✓' : ''}
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        emptyRows.map((_, idx) => (
                                            <tr key={idx} style={{height: '48px'}} className={"align-middle"}>
                                                <td className={"text-start px-2 py-2"}>&nbsp;</td>
                                                <td className={"py-2"}>&nbsp;</td>
                                                <td className={"py-2"}>&nbsp;</td>
                                                <td className={"py-2"}>&nbsp;</td>
                                                <td className={"py-2"}>&nbsp;</td>
                                            </tr>
                                        ))
                                    )}
                                    </tbody>
                                </Table>
                            </>
                        ) : (
                            <div className={"row my-4"}>
                                <div className={"col-12 text-center text-muted"}>Моля, изберете автомобил, за да попълните останалите полета.</div>
                            </div>
                        )}

                        {/* More spacing before signature for printed document */}
                        <div style={{height: '60px'}} />

                        <div className={"row mt-4"}>
                            <div className={"col-6 text-center"}>
                                ________________________________
                                <div>Подпис</div>
                            </div>
                            <div className={"col-6 text-center"}>
                                ________________________________
                                <div>Печат</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Modal>
    );
}
